import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaTrash } from 'react-icons/fa';
import PageForm from '../../templates/PageForm';
import SelectFilter from '../../organisms/SelectFilter';
import CopyPasteTable from '../../organisms/CopyPasteTable';
import TextEdit from '../../molecules/TextEdit';
import NumberEdit from '../../molecules/NumberEdit';
import CurrencyInput from '../../molecules/CurrencyInput';
import { AuthContext } from '../../../services/Auth/AuthContext';
import { fundingApi, CreateInstrumentItem, InstrumentProduct, Platform, RateType } from '../../../services/fundingApi';
import { extractAndConvertFrom, formatDateFromBRToISO } from '../../../utils/DateUtils';
import { currencyToNumber } from '../../../utils/currencyUtils';

// Mapeamento de colunas por corretora ao colar planilhas. Cada corretora
// exporta a emissão com nomes de coluna diferentes; este mapa traduz "NOME DA
// COLUNA NA PLANILHA DA CORRETORA" (em caixa alta) -> nome do campo do
// payload de POST /instruments (docs/api-contract.md). Conhecimento de
// negócio validado com a Tesouraria - não inventado.
//
// Indexado pelo NOME da plataforma (ver GET /platforms), não pelo id - é só
// uma ajuda de parsing local, o backend não sabe desse mapeamento. As chaves
// AGORA e NUBANK foram ajustadas para bater com a grafia real retornada por
// GET /platforms (seed de 2026-08-18: AGORA sem acento, NUBANK por extenso -
// o mapeamento antigo usava "ÁGORA"/"NU"). SANTCOR e GENIAL INST são
// plataformas reais sem mapeamento de colunas conhecido ainda - nesse caso o
// formulário cai no fallback manual (arrastar colunas) do CopyPasteTable.
//
// Campo "indexador_raw" recebe o texto crú da coluna de índice da corretora
// (ex: "PRE", "DI", "CDI", "PRÉ-FIXADO") - normalizeRateAndIndex() abaixo
// deriva rate_type (PRE/POS) e remuneration_index a partir dele no submit.
//
// Amostras reais recebidas em 2026-08-19 (Toro, Daycoval, Genial, BTG, PicPay)
// confirmaram os campos novos (issue_date, pu, financial_value, di_futuro) para
// essas 5 corretoras - ver docs/context/business-context.md secao 8.5. Para as
// corretoras sem amostra nova (XP, NIKOS, AGORA, ITAU, NUBANK, MIRAE) só o
// indexador foi renomeado para indexador_raw; pu/financial_value/issue_date
// continuam sem mapeamento conhecido para elas (ficam para arrastar/preencher
// manualmente até termos uma amostra real de cada uma).
const BROKER_FIELD_MAPPING: Record<string, Record<string, string>> = {
    DAYCOVAL: {
        'TIPO IF': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu',
        'PU DESAGIADO': 'pu_degassed',
        'VALOR': 'financial_value',
        'PREXDI': 'di_futuro',
    },
    XP: {
        'ATIVO': 'product',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA PRÉ-FIXADA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    // Amostra real 2026-08-19: Emissor, Ativo, Indexador, Prazo, Data Vencimento,
    // Taxa Emissor, Taxa Cliente, PU Emissão, Quantidade, Valor Aplicação,
    // Liquidez, PU Desagiado, CDI. Sem coluna de data de emissão - issue_date
    // cai no default (hoje), editável depois (ver toCreateItem).
    TORO: {
        'ATIVO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'PU EMISSÃO': 'pu',
        'QUANTIDADE': 'quantity',
        'VALOR APLICAÇÃO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CDI': 'di_futuro',
    },
    // Amostra real 2026-08-19 tinha uma coluna "FINANCEIRO" duplicada (bruta e
    // desagiada, mesmo nome) - resolvido em 2026-08-19 sem precisar confirmar
    // com a Genial: dá pra deduzir matematicamente qual é qual (verificado nas
    // 3 linhas da amostra) - QTDE × PU DE EMISSÃO bate exatamente com a 1ª
    // ocorrência de "FINANCEIRO" (ex: 73 × 1000 = 73.000,00) e QTDE × PU
    // DESAGIADO bate com a 2ª (ex: 73 × 999,06372357 = 72.931,65 - valor
    // desagiado, que nem pra Daycoval mapeamos). CopyPasteTable agora usa a
    // 1ª ocorrência de cada nome de coluna (fix nesta mesma revisão), então
    // mapear "FINANCEIRO" -> financial_value pega a bruta, correta.
    // "COMANDO" (número de controle da própria Genial) continua descartado -
    // decisão de negócio de 2026-08-19, ver business-context.md §8.5.
    // issue_date ainda não tem coluna confirmada (o cabeçalho colado tinha uma
    // coluna sem nome reconhecível ali) - mas como todas as linhas da amostra
    // tinham a mesma data (a do lote/lançamento), o default de "hoje" já usado
    // por Toro/Daycoval/INTER produz o mesmo resultado prático na maioria dos
    // casos - não é mais tratado como bloqueio, só uma lacuna de precisão.
    GENIAL: {
        'EMISSÃO': 'product',
        'VENCTO': 'due_date',
        'ÍNDICE': 'indexador_raw',
        'TAXA DE EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DE EMISSÃO': 'pu',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'CURVA': 'di_futuro',
    },
    NIKOS: {
        'PAPEL': 'product',
        'VENCIMENTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    AGORA: {
        'ATIVO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TX EMISSÃO': 'tax',
        'QTDE': 'quantity',
        'PU DESÁGIO': 'pu_degassed',
    },
    // Colunas reais confirmadas no bug do produto "CDB PRE" (2026-08-19): TIPO DE
    // CONTA, CONTA, ATIVO, EMISSOR, PRAZO/VENCIMENTO, FINANCEIRO, PU, QUANTIDADE,
    // TAXA CLIENTE (EMISSÃO), TAXA INTER DTVM (CHEIA), PU Desagiado, INDEXADOR,
    // DIAS C, DIAS U, DI, BROKER. Sem coluna de data de emissão.
    INTER: {
        'ATIVO': 'product',
        'PRAZO / VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE (EMISSÃO)': 'tax',
        'PU': 'pu',
        'QUANTIDADE': 'quantity',
        'FINANCEIRO': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
        'DI': 'di_futuro',
    },
    ITAU: {
        'PRODUTO': 'product',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU DESAGIADO': 'pu_degassed',
    },
    // Amostra real 2026-08-19: Produto, Prazo (Mês), Data Emissão, Data
    // Vencimento, DC, DU, Liquidez, Data de Início da Liquidez, Indexador,
    // Curva DI, Fee (a.a.), Taxa All, Taxa Cliente, Quantidade, PU Emissão,
    // Emissão (R$), PU Desagiado. Corretora nova nesta rodada (não tinha
    // mapeamento antes).
    BTG: {
        'PRODUTO': 'product',
        'DATA EMISSÃO': 'issue_date',
        'DATA VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'CURVA DI': 'di_futuro',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU EMISSÃO': 'pu',
        'EMISSÃO (R$)': 'financial_value',
        'PU DESAGIADO': 'pu_degassed',
    },
    NUBANK: {
        'TITULO': 'product',
        'VCTO': 'due_date',
        'INDICE': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QTDE TOTAL': 'quantity',
        'PU': 'pu_degassed',
    },
    MIRAE: {
        'ATIVO': 'product',
        'VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu_degassed',
    },
    // Corretora nova, cadastrada em 2026-08-19 (ver Platform). Amostra real:
    // Emissor, Ativo, Indexador, Emissão, Prazo Carência, Carência, Prazo (DC),
    // Dt. Vencimento, Taxa, Taxa Cliente, Quantidade, PU, Financeiro. NÃO manda
    // PU Desagiado - esse campo fica sem mapeamento (obrigatório, então o modo
    // colagem vai mostrar erro de coluna faltando; usar cadastro manual pra essa
    // corretora até termos a forma de calcular PU Desagiado). Datas em formato
    // americano M/D/AAAA - ver PICPAY em US_DATE_FORMAT_PLATFORMS.
    PICPAY: {
        'ATIVO': 'product',
        'EMISSÃO': 'issue_date',
        'DT. VENCIMENTO': 'due_date',
        'INDEXADOR': 'indexador_raw',
        'TAXA CLIENTE': 'tax',
        'QUANTIDADE': 'quantity',
        'PU': 'pu',
        'FINANCEIRO': 'financial_value',
    },
};

// PicPay envia datas em M/D/AAAA (formato americano) - todas as outras
// corretoras usam DD/MM/AAAA. Ver toIsoDate().
const US_DATE_FORMAT_PLATFORMS = new Set(['PICPAY']);

// Layout de destino da colagem - igual para qualquer plataforma (o que muda
// de plataforma para plataforma é o BROKER_FIELD_MAPPING acima). issue_date e
// di_futuro não são required: quando a corretora não manda a coluna (ver
// mapeamentos acima), issue_date cai no default de hoje e di_futuro fica nulo.
const ISSUANCE_HEADERS = [
    { label: 'Produto', field: 'product', required: true, format: 'text' },
    { label: 'Data Aplicação', field: 'issue_date', required: false, format: 'text' },
    { label: 'Vencimento', field: 'due_date', required: true, format: 'text' },
    { label: 'Indexador', field: 'indexador_raw', required: true, format: 'text' },
    { label: 'Taxa', field: 'tax', required: true, format: 'text' },
    { label: 'Quantidade', field: 'quantity', required: true, format: 'text' },
    { label: 'PU', field: 'pu', required: true, format: 'text' },
    { label: 'PU Desagiado', field: 'pu_degassed', required: true, format: 'text' },
    { label: 'Valor', field: 'financial_value', required: true, format: 'text' },
    { label: 'DI Futuro', field: 'di_futuro', required: false, format: 'text' },
];

const normalizePlatformName = (name: string): string => name.trim().toUpperCase();

type PastedRow = Record<string, string>;

interface Form {
    platformId: string;
    issuances?: PastedRow[];
}

// Converte "DD/MM/AAAA" (planilha, padrão BR) ou "M/D/AAAA" (PicPay, formato
// americano - ver US_DATE_FORMAT_PLATFORMS) ou "AAAA-MM-DD" (já no formato da
// API) para o formato ISO exigido por docs/api-contract.md ("YYYY-MM-DD").
const toIsoDate = (raw: string, useUsFormat: boolean): string => {
    const trimmed = (raw || '').trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    if (useUsFormat) {
        const usMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (usMatch) {
            const [, month, day, year] = usMatch;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) return formatDateFromBRToISO(trimmed);
    return trimmed;
};

const todayIso = (): string => new Date().toISOString().slice(0, 10);

// Algumas corretoras (ex: INTER) juntam produto e indexador na mesma coluna
// "ATIVO" (ex: "CDB PRE", "LCA PRE"), embora o indexador já venha separado na
// coluna INDEXADOR. Aqui extraímos só o código do produto (LCA/CDB), ignorando
// qualquer sufixo - sem isso "CDB PRE" nunca bate com o enum exato aceito pela API.
const normalizeProduct = (raw: string): string => {
    const upper = (raw || '').trim().toUpperCase();
    const match = upper.match(/^(LCA|CDB)\b/);
    return match ? match[1] : upper;
};

// Toda corretora usa uma grafia diferente para indicar pré vs pós-fixado na
// mesma coluna (Toro/PicPay: "PRE"/"DI"; Daycoval/BTG: "PRE"/"CDI"; Genial:
// "PRÉ-FIXADO"/"CDI") - nenhuma usa literalmente a palavra "POS". "DI" como
// indexador é convenção de mercado para papel indexado ao CDI, por isso mapeia
// pra remuneration_index="CDI" (ver docs/context/business-context.md secao 8.5).
const normalizeRateAndIndex = (raw: string): { rateType: RateType; remunerationIndex: string | null } => {
    const upper = (raw || '').trim().toUpperCase();
    if (upper.startsWith('PRE') || upper.includes('PRÉ')) {
        return { rateType: 'PRE', remunerationIndex: null };
    }
    if (upper === 'DI') {
        return { rateType: 'POS', remunerationIndex: 'CDI' };
    }
    return { rateType: 'POS', remunerationIndex: upper || null };
};

const toCreateItem = (row: PastedRow, platformId: string, platformName: string): CreateInstrumentItem => {
    const useUsFormat = US_DATE_FORMAT_PLATFORMS.has(normalizePlatformName(platformName));
    const { rateType, remunerationIndex } = normalizeRateAndIndex(row.indexador_raw);
    // Genial manda "0" literal em vez de vazio na coluna CURVA quando o papel é
    // PRE (sem DI Futuro aplicável) - trata como ausência de valor, não como
    // taxa zero de fato.
    const parsedDiFuturo = currencyToNumber(row.di_futuro || '');

    return {
        product: normalizeProduct(row.product) as InstrumentProduct,
        // Toro/Daycoval/INTER não mandam data de aplicação na planilha - cai no
        // default de hoje, editável depois (ver decisão do negócio em 2026-08-19).
        issue_date: row.issue_date ? toIsoDate(row.issue_date, useUsFormat) : todayIso(),
        due_date: toIsoDate(row.due_date, useUsFormat),
        rate_type: rateType,
        remuneration_index: remunerationIndex,
        tax: currencyToNumber(row.tax || ''),
        quantity: Math.round(currencyToNumber(row.quantity || '')),
        pu: currencyToNumber(row.pu || ''),
        pu_degassed: currencyToNumber(row.pu_degassed || ''),
        financial_value: currencyToNumber(row.financial_value || ''),
        di_futuro: parsedDiFuturo > 0 ? parsedDiFuturo : null,
        platform_id: platformId,
    };
};

const VALID_PRODUCTS: InstrumentProduct[] = ['LCA', 'CDB', 'DB_DI'];
const VALID_RATE_TYPES: RateType[] = ['PRE', 'POS'];

// DB_DI não tem PU Desagiado (liquida direto com a contraparte via CETIP) e
// pode ter um spread aditivo sobre o indexador (ex: "100% CDI + 1,85% Pré") -
// ver docs/context/business-context.md secao 8.2.
const requiresPuDegassed = (product: InstrumentProduct | ''): boolean => product !== 'DB_DI';

const PRODUCT_SELECT_OPTIONS = VALID_PRODUCTS.map((p) => ({ value: p, label: p === 'DB_DI' ? 'DB DI' : p }));
const RATE_TYPE_SELECT_OPTIONS = VALID_RATE_TYPES.map((r) => ({ value: r, label: r }));

// Validações de um único item, sem prefixo de linha - usado tanto pelo modo
// de colagem (que prefixa "Linha N:") quanto pelo cadastro manual (item único).
const itemProblems = (item: CreateInstrumentItem): string[] => {
    const problems: string[] = [];

    if (!VALID_PRODUCTS.includes(item.product)) {
        problems.push(`produto "${item.product || '(vazio)'}" inválido - aceito apenas LCA, CDB ou DB_DI.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.issue_date)) {
        problems.push(`data da aplicação "${item.issue_date || '(vazio)'}" fora do formato esperado.`);
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(item.due_date)) {
        problems.push(`vencimento "${item.due_date || '(vazio)'}" fora do formato esperado (DD/MM/AAAA).`);
    }
    if (!VALID_RATE_TYPES.includes(item.rate_type)) {
        problems.push('indexador (PRE/POS) inválido.');
    }
    if (item.rate_type === 'POS' && !item.remuneration_index) {
        problems.push('índice não informado (obrigatório quando o indexador é POS).');
    }
    if (!item.quantity || item.quantity <= 0) {
        problems.push('quantidade inválida.');
    }
    if (!item.tax || item.tax <= 0) {
        problems.push('taxa inválida.');
    }
    if (!item.pu || item.pu <= 0) {
        problems.push('PU inválido.');
    }
    if (requiresPuDegassed(item.product) && (!item.pu_degassed || item.pu_degassed <= 0)) {
        problems.push('PU desagiado inválido.');
    }
    if (!item.financial_value || item.financial_value <= 0) {
        problems.push('valor inválido.');
    }

    return problems;
};

const validateItems = (items: CreateInstrumentItem[], platformId: string): string[] => {
    const problems: string[] = [];

    if (!platformId) {
        problems.push('Selecione a corretora/plataforma.');
    }

    items.forEach((item, idx) => {
        itemProblems(item).forEach((p) => problems.push(`Linha ${idx + 1}: ${p}`));
    });

    return problems;
};

type ManualEntry = {
    product: InstrumentProduct | '';
    issueDate: string;
    dueDate: string;
    rateType: RateType;
    remunerationIndex: string;
    tax: number;
    quantity: number;
    pu: number;
    puDegassed: number;
    financialValue: number;
    diFuturo: number;
    additionalSpread: number;
};

const EMPTY_MANUAL_ENTRY: ManualEntry = {
    product: '',
    issueDate: '',
    dueDate: '',
    rateType: 'PRE',
    remunerationIndex: '',
    tax: 0,
    quantity: 0,
    pu: 0,
    puDegassed: 0,
    financialValue: 0,
    diFuturo: 0,
    additionalSpread: 0,
};

const IssuanceFormPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const form = useSignal<Form>({ platformId: '', issuances: [] });
    const isSending = useSignal(false);
    const buttonDisabled = useSignal(true);
    const hasErrors = useSignal(false);
    const currentMapping = useSignal<Record<string, string>>({});

    const platforms = useSignal<Platform[]>([]);
    const isLoadingPlatforms = useSignal(false);

    const entryMode = useSignal<'paste' | 'manual'>('paste');
    const manualEntry = useSignal<ManualEntry>(EMPTY_MANUAL_ENTRY);
    const manualItems = useSignal<CreateInstrumentItem[]>([]);

    useEffect(() => {
        const loadPlatforms = async () => {
            isLoadingPlatforms.value = true;
            const result = await fundingApi.listPlatforms();
            isLoadingPlatforms.value = false;

            if (!result.ok) {
                toast.error(`Não foi possível carregar as corretoras: ${result.message}`);
                return;
            }

            platforms.value = result.data.filter((p) => p.active);
        };
        loadPlatforms();
    }, []);

    useEffect(() => {
        const handleJson = (event: CustomEvent) => {
            const data = event.detail as PastedRow[];
            form.value = { ...form.value, issuances: data };
            buttonDisabled.value = hasErrors.value || data.length === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
        return () => window.removeEventListener('CopyPasteTable:jsonGenerated', handleJson as EventListener);
    }, []);

    useEffect(() => {
        const handleErrors = (event: CustomEvent) => {
            const errors = event.detail as unknown[];
            hasErrors.value = errors.length > 0;
            buttonDisabled.value = hasErrors.value || (form.value.issuances?.length || 0) === 0 || !form.value.platformId;
        };
        window.addEventListener('CopyPasteTable:errors', handleErrors as EventListener);
        return () => window.removeEventListener('CopyPasteTable:errors', handleErrors as EventListener);
    }, [form.value.issuances?.length]);

    const clearData = () => {
        window.dispatchEvent(new Event('CopyPasteTable:clearData'));
    };

    const selectPlatform = (value: string) => {
        const platform = platforms.value.find((p) => p.id === value);
        if (!platform) return;

        form.value = { ...form.value, platformId: value };
        currentMapping.value = BROKER_FIELD_MAPPING[normalizePlatformName(platform.name)] || {};
        clearData();
        manualItems.value = [];
    };

    // Trocar de modo descarta os dados do modo anterior - evita o cenário de
    // colar 5 linhas, trocar pra manual, adicionar 1 e enviar só essa 1 sem
    // o usuário perceber que as outras 5 ficaram de fora do envio.
    const switchMode = (mode: 'paste' | 'manual') => {
        if (mode === entryMode.value) return;
        entryMode.value = mode;
        clearData();
        manualItems.value = [];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const addManualItem = () => {
        if (!form.value.platformId) {
            toast.error('Selecione a corretora/plataforma antes de adicionar.');
            return;
        }

        const item: CreateInstrumentItem = {
            product: manualEntry.value.product as InstrumentProduct,
            issue_date: manualEntry.value.issueDate || todayIso(),
            due_date: manualEntry.value.dueDate,
            rate_type: manualEntry.value.rateType,
            remuneration_index: manualEntry.value.rateType === 'POS' ? manualEntry.value.remunerationIndex.trim().toUpperCase() : null,
            tax: manualEntry.value.tax,
            quantity: Math.round(manualEntry.value.quantity),
            pu: manualEntry.value.pu,
            pu_degassed: requiresPuDegassed(manualEntry.value.product) ? manualEntry.value.puDegassed : null,
            financial_value: manualEntry.value.financialValue,
            di_futuro: manualEntry.value.rateType === 'POS' && manualEntry.value.diFuturo ? manualEntry.value.diFuturo : null,
            additional_spread: manualEntry.value.product === 'DB_DI' && manualEntry.value.additionalSpread ? manualEntry.value.additionalSpread : null,
            platform_id: form.value.platformId,
        };

        const problems = itemProblems(item);
        if (problems.length > 0) {
            problems.forEach((p) => toast.error(p));
            return;
        }

        manualItems.value = [...manualItems.value, item];
        manualEntry.value = EMPTY_MANUAL_ENTRY;
    };

    const removeManualItem = (idx: number) => {
        manualItems.value = manualItems.value.filter((_, i) => i !== idx);
    };

    const isManualMode = () => entryMode.value === 'manual';

    const saveDisabled = isManualMode()
        ? (isSending.value || !form.value.platformId || manualItems.value.length === 0)
        : (isSending.value || buttonDisabled.value);

    const submitForm = async () => {
        if (saveDisabled) {
            toast.error('Por favor, corrija os erros antes de enviar o formulário.');
            return;
        }

        if (!user?.email) {
            toast.error('Não foi possível identificar o usuário logado.');
            return;
        }

        const platformId = form.value.platformId;
        const platformName = platforms.value.find((p) => p.id === platformId)?.name || '';
        const items = isManualMode()
            ? manualItems.value
            : (form.value.issuances || []).map((row) => toCreateItem(row, platformId, platformName));
        const problems = validateItems(items, platformId);
        if (problems.length > 0) {
            problems.slice(0, 5).forEach((message) => toast.error(message));
            return;
        }

        isSending.value = true;
        const result = await fundingApi.createInstruments(items, user.email);
        isSending.value = false;

        if (!result.ok) {
            if (result.errors.length > 0) {
                result.errors.slice(0, 5).forEach((err) => toast.error(`${err.fieldName}: ${err.message}`));
            } else {
                toast.error(result.message);
            }
            return;
        }

        toast.info(`${result.data.createdIds.length} emissão(ões) lançada(s) com sucesso.`);
        navigate('/funding/issuance/list');
    };

    const cancelForm = () => navigate(-1);

    // Conta CETIP é por produto desde 2026-08-19 (Daycoval usa contas diferentes
    // para LCA e CDB) - mostra as duas só quando divergem, senão só uma. DB_DI
    // (ex: SICOOB, ITAU UNIBANCO) só tem cetipAccountDi - LCA/CDB ficam null.
    const formatCetipLabel = (p: Platform): string => {
        if (p.cetipAccountLca && p.cetipAccountCdb && p.cetipAccountLca !== p.cetipAccountCdb) {
            return `CETIP LCA ${p.cetipAccountLca} / CDB ${p.cetipAccountCdb}`;
        }
        const account = p.cetipAccountLca || p.cetipAccountCdb || p.cetipAccountDi;
        return account ? `CETIP ${account}` : 'sem conta CETIP cadastrada';
    };

    const platformOptions = platforms.value.map((p) => ({
        value: p.id,
        label: `${p.name} — ${formatCetipLabel(p)}`,
    }));

    const isPicpay = normalizePlatformName(platforms.value.find((p) => p.id === form.value.platformId)?.name || '') === 'PICPAY';

    const hasKnownMapping = Object.keys(currentMapping.value).length > 0;

    return (
        <PageForm
            pageTitle='Lançar Emissões'
            apiVersion='Funding API'
            isLoading={false}
            breadcrumb={[{ label: 'Tesouraria', url: '/funding/issuance/list' }, { label: 'Lançar Emissões', url: '#' }]}
            controlSlot={(
                <div className='flex space-x-4 justify-end m-2'>
                    {!isManualMode() && form.value.issuances && form.value.issuances.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={clearData}
                            disabled={isSending.value}
                        >
                            Limpar Dados
                        </button>
                    )}
                    {isManualMode() && manualItems.value.length > 0 && (
                        <button
                            className='bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800'
                            onClick={() => { manualItems.value = []; }}
                            disabled={isSending.value}
                        >
                            Limpar Itens
                        </button>
                    )}
                    <button
                        className='text-black bg-slate-200 px-4 py-2 hover:bg-slate-400 rounded dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600'
                        onClick={cancelForm}
                        disabled={isSending.value}
                    >
                        Cancelar
                    </button>
                    <button
                        className='bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700'
                        onClick={submitForm}
                        disabled={saveDisabled}
                    >
                        Salvar
                    </button>
                </div>
            )}
        >
            <div className='container mx-auto p-4 space-y-4'>
                <SelectFilter
                    id='slc-broker'
                    label='Corretora:'
                    value={form.value.platformId}
                    onChange={selectPlatform}
                    defaultVoidOption
                    defaultLabel={isLoadingPlatforms.value ? 'Carregando corretoras...' : 'Selecione a corretora...'}
                    options={platformOptions}
                />

                {form.value.platformId && (
                    <div className='flex gap-2 border-b border-gray-200 dark:border-gray-700'>
                        <button
                            type='button'
                            onClick={() => switchMode('paste')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'paste'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Colar planilha
                        </button>
                        <button
                            type='button'
                            onClick={() => switchMode('manual')}
                            className={`px-4 py-2 text-sm border-b-2 -mb-px ${entryMode.value === 'manual'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400 font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                        >
                            Cadastro manual
                        </button>
                    </div>
                )}

                {form.value.platformId && entryMode.value === 'paste' && !hasKnownMapping && (
                    <p className='text-sm text-amber-600 dark:text-amber-400'>
                        Não há mapeamento automático de colunas conhecido para esta corretora ainda -
                        cole os dados e arraste as colunas na ordem correta (Produto, Data Aplicação, Vencimento,
                        Indexador, Taxa, Quantidade, PU, PU Desagiado, Valor, DI Futuro).
                    </p>
                )}

                {form.value.platformId && entryMode.value === 'paste' && isPicpay && (
                    <p className='text-sm text-amber-600 dark:text-amber-400'>
                        A PicPay não envia PU Desagiado na planilha - essa coluna vai ficar vazia e a linha será
                        rejeitada no modo colagem. Use o "Cadastro manual" para essa corretora até termos uma forma
                        de calcular o PU Desagiado automaticamente.
                    </p>
                )}

                {form.value.platformId && entryMode.value === 'paste' && (
                    <CopyPasteTable
                        fixedHeaders={ISSUANCE_HEADERS}
                        allowDuplicates={true}
                        // Sem mapeamento conhecido (ex: SANTCOR, GENIAL INST), não passar
                        // columnMapping - um objeto vazio faria o CopyPasteTable tratar a
                        // 1ª linha colada como cabeçalho e zerar todos os campos (nenhuma
                        // coluna bateria). undefined cai no fallback manual (drag-and-drop).
                        columnMapping={hasKnownMapping ? currentMapping.value : undefined}
                    />
                )}

                {form.value.platformId && entryMode.value === 'manual' && (
                    <div className='space-y-4'>
                        <div className='grid grid-cols-3 gap-4'>
                            <SelectFilter
                                id='manual-product'
                                label='Produto'
                                value={manualEntry.value.product}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, product: v as InstrumentProduct }; }}
                                defaultVoidOption
                                defaultLabel='Selecione...'
                                options={PRODUCT_SELECT_OPTIONS}
                            />
                            <TextEdit
                                id='manual-issue-date'
                                label='Data da Aplicação'
                                type='date'
                                value={manualEntry.value.issueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, issueDate: e.target.value }; }}
                                placeholder={todayIso()}
                            />
                            <TextEdit
                                id='manual-due-date'
                                label='Vencimento'
                                type='date'
                                value={manualEntry.value.dueDate}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, dueDate: e.target.value }; }}
                            />
                            <SelectFilter
                                id='manual-rate-type'
                                label='Indexador'
                                value={manualEntry.value.rateType}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, rateType: v as RateType }; }}
                                options={RATE_TYPE_SELECT_OPTIONS}
                            />
                            {manualEntry.value.rateType === 'POS' && (
                                <TextEdit
                                    id='manual-index'
                                    label='Índice (ex: CDI)'
                                    value={manualEntry.value.remunerationIndex}
                                    onChange={(e) => { manualEntry.value = { ...manualEntry.value, remunerationIndex: e.target.value }; }}
                                />
                            )}
                            <CurrencyInput
                                id='manual-tax'
                                label='Taxa (%)'
                                value={manualEntry.value.tax}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, tax: v }; }}
                                fractionDigits={2}
                            />
                            <NumberEdit
                                id='manual-quantity'
                                label='Quantidade'
                                value={manualEntry.value.quantity || ''}
                                onChange={(e) => { manualEntry.value = { ...manualEntry.value, quantity: parseInt(e.target.value || '0', 10) }; }}
                            />
                            <CurrencyInput
                                id='manual-pu'
                                label='PU (R$)'
                                value={manualEntry.value.pu}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, pu: v }; }}
                                fractionDigits={2}
                            />
                            {requiresPuDegassed(manualEntry.value.product) && (
                                <CurrencyInput
                                    id='manual-pu-degassed'
                                    label='PU Desagiado (R$)'
                                    value={manualEntry.value.puDegassed}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, puDegassed: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                            <CurrencyInput
                                id='manual-financial-value'
                                label='Valor (R$)'
                                value={manualEntry.value.financialValue}
                                onChange={(v) => { manualEntry.value = { ...manualEntry.value, financialValue: v }; }}
                                fractionDigits={2}
                            />
                            {manualEntry.value.rateType === 'POS' && (
                                <CurrencyInput
                                    id='manual-di-futuro'
                                    label='DI Futuro (%) - opcional'
                                    value={manualEntry.value.diFuturo}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, diFuturo: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                            {manualEntry.value.product === 'DB_DI' && (
                                <CurrencyInput
                                    id='manual-additional-spread'
                                    label='Spread Adicional (%) - opcional'
                                    value={manualEntry.value.additionalSpread}
                                    onChange={(v) => { manualEntry.value = { ...manualEntry.value, additionalSpread: v }; }}
                                    fractionDigits={2}
                                />
                            )}
                        </div>
                        <div>
                            <button
                                type='button'
                                onClick={addManualItem}
                                className='px-4 py-2 text-sm border border-gray-600 text-gray-600 rounded hover:bg-gray-100 dark:border-gray-400 dark:text-gray-400 dark:hover:bg-slate-700'
                            >
                                Adicionar emissão
                            </button>
                        </div>

                        {manualItems.value.length > 0 && (
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700'>
                                        <th className='pb-2'>Produto</th>
                                        <th className='pb-2'>Aplicação</th>
                                        <th className='pb-2'>Vencimento</th>
                                        <th className='pb-2'>Indexador</th>
                                        <th className='pb-2'>Taxa</th>
                                        <th className='pb-2 text-right'>Quantidade</th>
                                        <th className='pb-2 text-right'>PU</th>
                                        <th className='pb-2 text-right'>PU Desagiado</th>
                                        <th className='pb-2 text-right'>Valor</th>
                                        <th className='pb-2'></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manualItems.value.map((item, idx) => (
                                        <tr key={idx} className='border-b border-gray-100 dark:border-slate-700'>
                                            <td className='py-2'>{item.product}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.issue_date)}</td>
                                            <td className='py-2'>{extractAndConvertFrom(item.due_date)}</td>
                                            <td className='py-2'>{item.rate_type === 'POS' ? `${item.remuneration_index} (POS)` : 'PRE'}</td>
                                            <td className='py-2'>{item.tax.toFixed(2)}%</td>
                                            <td className='py-2 text-right'>{item.quantity}</td>
                                            <td className='py-2 text-right'>{item.pu.toFixed(2)}</td>
                                            <td className='py-2 text-right'>{item.pu_degassed !== null ? item.pu_degassed.toFixed(2) : '-'}</td>
                                            <td className='py-2 text-right'>{item.financial_value.toFixed(2)}</td>
                                            <td className='py-2 text-right'>
                                                <button
                                                    type='button'
                                                    onClick={() => removeManualItem(idx)}
                                                    className='text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400'
                                                    title='Remover'
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>
        </PageForm>
    );
};

export default IssuanceFormPage;
